/**
 * Cookie consent banner. Shown once until the user picks Accept or Decline.
 *
 * Accept → GA4 script loads, future page_view + custom events flow.
 * Decline → analytics module stays a no-op for the rest of the session and
 *           every future visit (we persist the choice in localStorage).
 *
 * The banner is intentionally non-blocking — it floats at the bottom and the
 * rest of the page is interactive. We don't pre-tick "accept" because that's
 * not consent, and the legal pages claim explicit opt-in (LegalPages.tsx).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { analytics, getConsent, setConsent } from '@/lib/analytics';
import { Button } from '@/components/ui/button';

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const c = getConsent();
    setVisible(c === null);
    // If consent is already granted on a fresh page load, prime GA so the
    // first page_view fires for this session (the analytics module loads its
    // own gtag script lazily on the first call).
    if (c === 'granted') analytics.trackPageView(window.location.pathname);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded-lg border bg-background/95 p-4 shadow-lg backdrop-blur"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          We use a single, IP-truncated analytics cookie to understand how
          visitors use the site. No ads, no third-party trackers.{' '}
          <Link to="/cookies" className="underline">
            Cookie policy
          </Link>
          .
        </p>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setConsent('denied');
              setVisible(false);
            }}
          >
            Decline
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setConsent('granted');
              analytics.trackPageView(window.location.pathname);
              setVisible(false);
            }}
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
